import { NgIf } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { DataUtils, FileLoadError } from '@core/util/data-util.service';
import { AbstractEditComponent } from '@flowinn/components/filter/abstract-filter/abstract-edit.component';
import { EditComponent } from '@flowinn/components/pages/edit/edit.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateDirective } from '@language/translate.directive';
import { ProgramEnum, ProgramEnumOptions } from '@model/enumerations/program-enum.model';
import {
  ProgramModality,
  ProgramModalityCuidate2Options,
  ProgramModalityCuidateOptions,
  ProgramModalityOtlOptions,
} from '@model/enumerations/program-modality.model';
import { IProgramNews, ProgramNews } from '@model/program-news.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ProgramNewsService } from './program-news.service';

@Component({
  selector: 'jhi-program-news-update',
  templateUrl: './program-news-update.component.html',
  imports: [
    TranslateDirective,
    EditComponent,
    ReactiveFormsModule,
    NgSelectModule,
    TranslateModule,
    EditorModule,
    CalendarModule,
    FontAwesomeModule,
    NgIf,
    DropdownModule,
  ],
  standalone: true,
})
export class ProgramNewsUpdateComponent extends AbstractEditComponent<IProgramNews> {
  record: IProgramNews = new ProgramNews();
  ProgramEnum = ProgramEnum;
  ProgramModality = ProgramModality;
  editForm = this.fb.group({
    id: [],
    programEnum: [null, [Validators.required]],
    programModality: [null, [Validators.required]],
    position: [],
    titlePT: [],
    titleEN: [],
    descriptionPT: [],
    descriptionEN: [],
    active: [],
    startDate: [],
    endDate: [],
    editedDate: [],
    image: [],
    imageContentType: [],
    imageSmall: [],
    imageSmallContentType: [],
    imagesId: [],
  });

  constructor(protected dataUtils: DataUtils, protected programNewsService: ProgramNewsService, protected injector: Injector) {
    super(injector, 'programNews', 'programs/program-news');
  }

  onInit(): void {
    if (this.record && !this.record.id) {
      const today = dayjs().startOf('day');
      this.record.startDate = today;
      this.record.endDate = today;
      this.record.editedDate = today;
    }
  }

  updateForm(record: IProgramNews): void {
    this.editForm.patchValue({
      id: record.id,
      programEnum: record.programEnum,
      programModality: record.programModality,
      position: record.position,
      titlePT: record.titlePT,
      titleEN: record.titleEN,
      descriptionPT: record.descriptionPT,
      descriptionEN: record.descriptionEN,
      active: record.active,
      startDate: record.startDate ? record.startDate.toDate() : null,
      endDate: record.endDate ? record.endDate.toDate() : null,
      editedDate: record.editedDate ? record.editedDate.toDate() : null,
      image: record.image,
      imageContentType: record.imageContentType,
      imageSmall: record.imageSmall,
      imageSmallContentType: record.imageSmallContentType,
      imagesId: record.imagesId,
    });
  }

  byteSize(base64String: string): string {
    return this.dataUtils.byteSize(base64String);
  }

  openFile(contentType: string, base64String: string): void {
    this.dataUtils.openFile(contentType, base64String);
  }

  setFileData(event: Event, field: string, isImage: boolean): void {
    this.dataUtils.loadFileToForm(event, this.editForm, field, isImage).subscribe(null, (err: FileLoadError) => {
      this.notificationService.error(err.message);
    });
  }

  save(): void {
    this.isSaving = true;
    const programNews = this.createFromForm();
    if (programNews.id !== undefined) {
      this.subscribeToSaveResponse(this.programNewsService.update(programNews));
    } else {
      this.subscribeToSaveResponse(this.programNewsService.create(programNews));
    }
  }

  private createFromForm(): IProgramNews {
    return {
      ...new ProgramNews(),
      id: this.editForm.get(['id'])!.value,
      programEnum: this.editForm.get(['programEnum'])!.value,
      programModality: this.editForm.get(['programModality'])!.value,
      position: this.editForm.get(['position'])!.value,
      titlePT: this.editForm.get(['titlePT'])!.value,
      titleEN: this.editForm.get(['titleEN'])!.value,
      descriptionPT: this.editForm.get(['descriptionPT'])!.value,
      descriptionEN: this.editForm.get(['descriptionEN'])!.value,
      active: this.editForm.get(['active'])!.value,
      startDate: this.editForm.get(['startDate'])!.value,
      endDate: this.editForm.get(['endDate'])!.value,
      editedDate: this.editForm.get(['editedDate'])!.value,
      imageContentType: this.editForm.get(['imageContentType'])!.value,
      image: this.editForm.get(['image'])!.value,
      imageSmallContentType: this.editForm.get(['imageSmallContentType'])!.value,
      imageSmall: this.editForm.get(['imageSmall'])!.value,
      imagesId: this.editForm.get(['imagesId'])!.value,
    };
  }

  protected readonly ProgramModalityCuidateOptions = ProgramModalityCuidateOptions;
  protected readonly ProgramModalityCuidate2Options = ProgramModalityCuidate2Options;
  protected readonly ProgramModalityOtlOptions = ProgramModalityOtlOptions;
  protected readonly ProgramEnumOptions = ProgramEnumOptions;
}
